import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Grid, IconButton, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem, CircularProgress, Tabs, Tab,
  InputAdornment, Checkbox, FormControlLabel, FormGroup, Divider,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon, Search as SearchIcon, Person as PersonIcon,
  Add as AddIcon, DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import {
  normalizePatientFamilyRelatives,
  sanitizeFamilyRelativesForSave,
  type PatientFamilyRelative,
} from '../utils/patientFamily';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import {
  formatTaxIdInput,
  patientTaxIdRequiredMessage,
  taxIdGuardianLabel,
  taxIdRequiredLabel,
} from '../utils/taxIdRegional';
import { phoneExampleE164, phoneInputHelperText } from '../utils/phoneRegional';
import { isPortugalMarket } from '../config/marketRegion';
import { postalCodeLabel } from '../utils/addressRegional';
import { MOBILE_FORM_SURFACE_CLASS, mobileFullScreenDialogPaperSx } from '../utils/mobileFormLayout';
import {
  PATIENT_FORM_THEME,
  patientFormDialogPaperSx,
  patientFormDialogHeaderSx,
  patientFormDialogTitleSx,
  patientFormHeaderIconSx,
  patientFormCloseButtonSx,
  patientFormDialogContentSx,
  patientFormTabsSx,
  patientFormSectionTitleSx,
  patientFormCaptionSx,
  patientFormCaptionMutedSx,
  patientFormDividerCaptionSx,
  patientFormPrimaryButtonSx,
  patientFormCancelButtonSx,
  patientFormDialogFooterSx,
  patientFormCheckboxSx,
} from './patient/patientFormTheme';
import PatientSaveErrorDialog, {
  resolvePatientSaveError,
  type PatientSaveErrorInfo,
} from './patient/PatientSaveErrorDialog';
import PatientChatLinkPrompt, { type PatientChatLinkTarget } from './patient/PatientChatLinkPrompt';

export const emptyPatientForm = {
  full_name: '', social_name: '', cpf: '', rg: '', birth_date: '', gender: '',
  marital_status: '', profession: '', mother_name: '', father_name: '', patient_source: '', patient_source_id: '',
  family_relatives: [] as PatientFamilyRelative[],
  email: '', phone: '55', phone_secondary: '',
  whatsapp: '55', companion_name: '', companion_relation: '',
  vital_blood_pressure: '', vital_heart_rate: '', vital_glucose: '',
  address_street: '', address_number: '', address_complement: '',
  address_neighborhood: '', address_city: '', address_state: '', address_zip: '',
  blood_type: '', allergies: [] as string[], chronic_conditions: [] as string[],
  medications: [] as string[], medical_notes: '',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '',
  insurance_id: '', insurance_plan: '', insurance_number: '', insurance_validity: '',
  guardian_name: '', guardian_cpf: '', guardian_phone: '', guardian_relation: '',
  notes: '',
  // Pré-consulta (Oftalmologia) — persistido em metadata.ophthalmology_preconsult
  op_reason: '', op_has: false, op_dm: false, op_glaucoma: false,
  op_faco_od: false, op_faco_oe: false, op_hf: '', op_av_cc: false, op_av_sc: false,
};

const OPHTHALMOLOGY = 'oftalmologia';

/** Valor especial do select quando a origem não está na lista cadastrada. */
const PATIENT_SOURCE_CUSTOM = '__custom__';

/** Evita sobreposição do ícone nativo do calendário sobre o ano em inputs type="date". */
const dateInputFieldSx = {
  '& input[type="date"]': {
    paddingRight: '2.75rem',
    minHeight: '1.4375em',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    marginLeft: 8,
    cursor: 'pointer',
    opacity: 0.75,
  },
} as const;

function calculateAgeYears(birthDate: string): number | null {
  if (!birthDate) return null;
  const birth = new Date(`${String(birthDate).split('T')[0]}T12:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) years--;
  return years < 0 ? null : years;
}

function calculateAge(birthDate: string): string {
  const years = calculateAgeYears(birthDate);
  if (years === null) return '';
  if (years < 1) {
    const birth = new Date(`${String(birthDate).split('T')[0]}T12:00:00`);
    const now = new Date();
    let months = now.getMonth() - birth.getMonth() + 12 * (now.getFullYear() - birth.getFullYear());
    if (now.getDate() < birth.getDate()) months--;
    return `${Math.max(months, 0)} ${months === 1 ? 'mês' : 'meses'}`;
  }
  return `${years} ${years === 1 ? 'ano' : 'anos'}`;
}

function isPatientMinor(birthDate: string): boolean {
  const years = calculateAgeYears(birthDate);
  return years !== null && years < 18;
}

export interface PatientFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** `null` = novo paciente */
  patientToEdit: Record<string, any> | null;
  onSaved?: (info: { mode: 'create' | 'update' }) => void;
  onError?: (message: string) => void;
}

const PatientFormDialog: React.FC<PatientFormDialogProps> = ({
  open,
  onClose,
  patientToEdit,
  onSaved,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState(emptyPatientForm);
  const [formTabValue, setFormTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [patientSources, setPatientSources] = useState<Array<{ id: string; name: string }>>([]);
  const [patientSourceCustomMode, setPatientSourceCustomMode] = useState(false);
  const [clinicSpecialty, setClinicSpecialty] = useState<string | null>(null);
  const [relaxRequiredFields, setRelaxRequiredFields] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const [saveError, setSaveError] = useState<PatientSaveErrorInfo | null>(null);
  const [chatLinkTarget, setChatLinkTarget] = useState<PatientChatLinkTarget | null>(null);
  const cepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOphthalmology = clinicSpecialty === OPHTHALMOLOGY;
  const ADDRESS_TAB = 1;
  const reqLabel = (label: string) => (relaxRequiredFields ? label.replace(/ \*$/, '') : label);

  const fetchCep = useCallback(async (raw: string) => {
    const cep = raw.replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepLoading(true);
    setCepError('');
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) {
        setCepError(`${postalCodeLabel()} não encontrado`);
        return;
      }
      setFormData((prev) => ({
        ...prev,
        address_street: data.logradouro || prev.address_street,
        address_neighborhood: data.bairro || prev.address_neighborhood,
        address_city: data.localidade || prev.address_city,
        address_state: data.uf || prev.address_state,
        address_zip: raw,
      }));
      setFormTabValue(ADDRESS_TAB);
    } catch {
      setCepError(`Erro ao buscar ${postalCodeLabel().toLowerCase()}`);
    } finally {
      setCepLoading(false);
    }
  }, []);

  const handleCepChange = (value: string) => {
    setCepError('');
    setFormData((prev) => ({ ...prev, address_zip: value }));
    if (cepTimerRef.current) clearTimeout(cepTimerRef.current);
    const digits = value.replace(/\D/g, '');
    if (!isPortugalMarket() && digits.length === 8) {
      cepTimerRef.current = setTimeout(() => fetchCep(value), 400);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (patientToEdit) {
      const op = (patientToEdit.metadata && typeof patientToEdit.metadata === 'object'
        ? patientToEdit.metadata.ophthalmology_preconsult
        : null) || {};
      setFormData({
        ...emptyPatientForm,
        ...patientToEdit,
        allergies: patientToEdit.allergies || [],
        chronic_conditions: patientToEdit.chronic_conditions || [],
        medications: patientToEdit.medications || [],
        family_relatives: normalizePatientFamilyRelatives(patientToEdit.family_relatives),
        op_reason: op.reason || '',
        op_has: !!op.has,
        op_dm: !!op.dm,
        op_glaucoma: !!op.glaucoma,
        op_faco_od: !!op.faco_od,
        op_faco_oe: !!op.faco_oe,
        op_hf: op.hf || '',
        op_av_cc: !!op.av_cc,
        op_av_sc: !!op.av_sc,
      });
      setPatientSourceCustomMode(
        !patientToEdit.patient_source_id && Boolean(String(patientToEdit.patient_source || '').trim()),
      );
    } else {
      setFormData(emptyPatientForm);
      setPatientSourceCustomMode(false);
    }
    setFormTabValue(0);
  }, [open, patientToEdit]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/patient-sources'));
        const list = res.data?.success !== false ? (res.data?.data ?? []) : [];
        setPatientSources(Array.isArray(list) ? list.map((x: any) => ({ id: x.id, name: x.name })) : []);
      } catch {
        setPatientSources([]);
      }
    })();
  }, [open]);

  const loadClinicSettingsForForm = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/settings'));
      const d = res.data?.data;
      const v = d?.clinic_medical_specialty;
      setClinicSpecialty(typeof v === 'string' && v.trim() ? v.trim() : null);
      setRelaxRequiredFields(Boolean(d?.patient_form_relax_required_fields));
    } catch {
      setClinicSpecialty(null);
      setRelaxRequiredFields(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadClinicSettingsForForm();
    const onSettingsSaved = () => { void loadClinicSettingsForForm(); };
    window.addEventListener('clinic-settings:saved', onSettingsSaved);
    return () => window.removeEventListener('clinic-settings:saved', onSettingsSaved);
  }, [open, loadClinicSettingsForForm]);

  const editingId = patientToEdit?.id ?? null;

  const setField = (field: string, value: any) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!relaxRequiredFields) {
      const requireText = (val: string) => Boolean(String(val ?? '').trim());
      const requireWhatsapp = (val: string) => String(val ?? '').replace(/\D/g, '').length > 2;
      const minor = isPatientMinor(formData.birth_date);
      const checks: Array<{ ok: boolean; tab: number; msg: string }> = [
        { ok: requireText(formData.full_name), tab: 0, msg: 'Nome do paciente é obrigatório' },
        { ok: requireText(formData.cpf), tab: 0, msg: patientTaxIdRequiredMessage() },
        { ok: requireText(formData.birth_date), tab: 0, msg: 'Data de nascimento é obrigatória' },
        { ok: requireText(formData.gender), tab: 0, msg: 'Gênero é obrigatório' },
        { ok: !minor || requireText(formData.mother_name), tab: 0, msg: 'Nome da mãe é obrigatório para pacientes menores de 18 anos' },
        { ok: requireWhatsapp(formData.whatsapp), tab: 0, msg: 'WhatsApp é obrigatório' },
        { ok: requireText(formData.patient_source_id) || requireText(formData.patient_source), tab: 0, msg: 'Origem do lead é obrigatória' },
      ];
      const failed = checks.find((c) => !c.ok);
      if (failed) {
        setFormTabValue(failed.tab);
        setSaveError({
          title: 'Campos obrigatórios',
          message: failed.msg,
          focusCpf: failed.msg.toLowerCase().includes(patientTaxIdRequiredMessage().split(' ')[0]!.toLowerCase()),
        });
        return;
      }
    }

    try {
      setSaving(true);
      setSaveError(null);
      const {
        op_reason, op_has, op_dm, op_glaucoma, op_faco_od, op_faco_oe, op_hf, op_av_cc, op_av_sc,
        ...rest
      } = formData;
      const payload: any = { ...rest };

      if (relaxRequiredFields && !String(payload.full_name || '').trim()) {
        payload.full_name = '(Sem nome)';
      }

      payload.family_relatives = sanitizeFamilyRelativesForSave(payload.family_relatives);

      if (payload.patient_source_id) {
        payload.patient_source = null;
      } else {
        payload.patient_source_id = null;
        payload.patient_source = String(payload.patient_source || '').trim() || null;
      }

      // Pré-consulta oftalmológica → metadata (preserva demais chaves de metadata)
      if (isOphthalmology) {
        const baseMeta = (patientToEdit?.metadata && typeof patientToEdit.metadata === 'object') ? patientToEdit.metadata : {};
        payload.metadata = {
          ...baseMeta,
          ophthalmology_preconsult: {
            reason: op_reason || '',
            has: !!op_has, dm: !!op_dm, glaucoma: !!op_glaucoma,
            faco_od: !!op_faco_od, faco_oe: !!op_faco_oe,
            hf: op_hf || '',
            av_cc: !!op_av_cc, av_sc: !!op_av_sc,
          },
        };
      }

      if (editingId) {
        await axios.put(getApiUrl(`/clinic/patients/${editingId}`), payload);
        onSaved?.({ mode: 'update' });
        if (!patientToEdit?.contact_id) {
          setChatLinkTarget({ id: String(editingId), name: payload.full_name });
        }
      } else {
        const { data: created } = await axios.post(getApiUrl('/clinic/patients'), payload);
        onSaved?.({ mode: 'create' });
        const createdId = created?.data?.id ?? created?.id;
        if (createdId) setChatLinkTarget({ id: String(createdId), name: payload.full_name });
      }
      onClose();
    } catch (err: any) {
      const resolved = resolvePatientSaveError(err);
      if (resolved.focusCpf) setFormTabValue(0);
      setSaveError(resolved);
      onError?.(resolved.message);
    } finally {
      setSaving(false);
    }
  };

  const ageText = calculateAge(formData.birth_date);
  const patientIsMinor = isPatientMinor(formData.birth_date);
  const patientSourceSelectValue = patientSourceCustomMode
    ? PATIENT_SOURCE_CUSTOM
    : (formData.patient_source_id || '');
  const isCustomPatientSource = patientSourceCustomMode;

  return (
    <>
    <PatientChatLinkPrompt patient={chatLinkTarget} onDone={() => setChatLinkTarget(null)} />
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: isMobile ? mobileFullScreenDialogPaperSx : patientFormDialogPaperSx(isMobile),
      }}
    >
      <DialogTitle sx={patientFormDialogHeaderSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <PersonIcon sx={patientFormHeaderIconSx} />
          <Typography variant="h6" sx={patientFormDialogTitleSx}>
            {editingId ? 'Editar Paciente' : 'Novo Paciente'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Fechar" sx={patientFormCloseButtonSx}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        className={isMobile ? MOBILE_FORM_SURFACE_CLASS : undefined}
        sx={patientFormDialogContentSx}
      >
        <Tabs
          value={formTabValue}
          onChange={(_, v) => setFormTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={patientFormTabsSx}
        >
          <Tab label="Dados Pessoais" />
          <Tab label="Endereço" />
          <Tab label="Saúde" />
          <Tab label="Sinais Vitais" />
          <Tab label="Convênio" />
          <Tab label="Responsável" />
          {isOphthalmology && <Tab label="Pré-Consulta" />}
        </Tabs>

        {formTabValue === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label={reqLabel('Nome Completo *')} value={formData.full_name} onChange={(e) => setField('full_name', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nome Social" value={formData.social_name} onChange={(e) => setField('social_name', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label={reqLabel(taxIdRequiredLabel())} value={formData.cpf} onChange={(e) => setField('cpf', formatTaxIdInput(e.target.value))} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="RG" value={formData.rg} onChange={(e) => setField('rg', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={reqLabel('Data de Nascimento *')}
                type="date"
                value={formData.birth_date}
                onChange={(e) => setField('birth_date', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={dateInputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Idade" value={ageText} size="small" InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>{reqLabel('Gênero *')}</InputLabel>
                <Select value={formData.gender} label={reqLabel('Gênero *')} onChange={(e) => setField('gender', e.target.value)}>
                  <MenuItem value="masculine">Masculino</MenuItem><MenuItem value="feminine">Feminino</MenuItem>
                  <MenuItem value="other">Outro</MenuItem><MenuItem value="prefer_not_say">Prefiro não dizer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small"><InputLabel>Estado Civil</InputLabel>
                <Select value={formData.marital_status} label="Estado Civil" onChange={(e) => setField('marital_status', e.target.value)}>
                  <MenuItem value="single">Solteiro(a)</MenuItem><MenuItem value="married">Casado(a)</MenuItem>
                  <MenuItem value="divorced">Divorciado(a)</MenuItem><MenuItem value="widowed">Viúvo(a)</MenuItem>
                  <MenuItem value="other">Outro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Profissão" value={formData.profession} onChange={(e) => setField('profession', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={relaxRequiredFields ? 'Nome da Mãe' : (patientIsMinor ? 'Nome da Mãe *' : 'Nome da Mãe')}
                value={formData.mother_name}
                onChange={(e) => setField('mother_name', e.target.value)}
                size="small"
                helperText={
                  relaxRequiredFields
                    ? undefined
                    : (patientIsMinor ? 'Obrigatório para menores de 18 anos' : 'Opcional para maiores de idade')
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome do Pai"
                value={formData.father_name}
                onChange={(e) => setField('father_name', e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Telefone" value={formData.phone} onChange={(e) => setField('phone', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefone adicional (informação)"
                value={formData.phone_secondary}
                onChange={(e) => setField('phone_secondary', e.target.value)}
                size="small"
                helperText="Opcional. Não entra em WhatsApp, busca nem vínculo com inbox."
              />
            </Grid>
            <Grid item xs={12}><Divider sx={{ my: 0.5 }}><Typography variant="caption" sx={{ color: '#94a3b8' }}>Rastreio</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label={reqLabel('WhatsApp *')} value={formData.whatsapp} onChange={(e) => setField('whatsapp', e.target.value)} size="small" placeholder={phoneExampleE164()} helperText={phoneInputHelperText()} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={formData.email} onChange={(e) => setField('email', e.target.value)} size="small" /></Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>{reqLabel('Origem do Lead *')}</InputLabel>
                <Select
                  label={reqLabel('Origem do Lead *')}
                  value={patientSourceSelectValue}
                  onChange={(e) => {
                    const v = String(e.target.value);
                    if (v === PATIENT_SOURCE_CUSTOM) {
                      setPatientSourceCustomMode(true);
                      setFormData({
                        ...formData,
                        patient_source_id: '',
                        patient_source: formData.patient_source || '',
                      });
                    } else if (!v) {
                      setPatientSourceCustomMode(false);
                      setFormData({ ...formData, patient_source_id: '', patient_source: '' });
                    } else {
                      setPatientSourceCustomMode(false);
                      setFormData({ ...formData, patient_source_id: v, patient_source: '' });
                    }
                  }}
                >
                  <MenuItem value="">Não informado</MenuItem>
                  {patientSources.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                  <MenuItem value={PATIENT_SOURCE_CUSTOM}>Outra (texto livre)</MenuItem>
                </Select>
              </FormControl>
              {isCustomPatientSource && (
                <TextField
                  fullWidth
                  size="small"
                  label="Descreva a origem *"
                  placeholder="Ex.: Indicação da Dra. Maria, feira de saúde, parceiro local..."
                  value={formData.patient_source}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      patient_source_id: '',
                      patient_source: e.target.value,
                    })
                  }
                  sx={{ mt: 1 }}
                  helperText="Use quando a origem não estiver na lista cadastrada"
                />
              )}
              <Typography variant="caption" sx={{ ...patientFormCaptionMutedSx, mt: 0.5, display: 'block' }}>
                Cadastrar opções fixas em Clínica › Origens de pacientes
              </Typography>
            </Grid>
            <Grid item xs={12}><Divider sx={{ my: 0.5 }}><Typography variant="caption" sx={patientFormDividerCaptionSx}>Acompanhante</Typography></Divider></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Acompanhante" value={formData.companion_name} onChange={(e) => setField('companion_name', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Relação do acompanhante" value={formData.companion_relation} onChange={(e) => setField('companion_relation', e.target.value)} size="small" /></Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }}>
                <Typography variant="caption" sx={patientFormDividerCaptionSx}>Outros familiares</Typography>
              </Divider>
            </Grid>
            {(formData.family_relatives || []).map((rel, idx) => (
              <React.Fragment key={`fam-${idx}`}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nome"
                    value={rel.name}
                    onChange={(e) => {
                      const next = [...(formData.family_relatives || [])];
                      next[idx] = { ...next[idx], name: e.target.value };
                      setField('family_relatives', next);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Parentesco"
                    value={rel.relation || ''}
                    onChange={(e) => {
                      const next = [...(formData.family_relatives || [])];
                      next[idx] = { ...next[idx], relation: e.target.value };
                      setField('family_relatives', next);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Profissão (opc.)"
                    value={rel.profession || ''}
                    onChange={(e) => {
                      const next = [...(formData.family_relatives || [])];
                      next[idx] = { ...next[idx], profession: e.target.value };
                      setField('family_relatives', next);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    aria-label="Remover familiar"
                    onClick={() => {
                      const next = (formData.family_relatives || []).filter((_, i) => i !== idx);
                      setField('family_relatives', next);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))}
            <Grid item xs={12}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setField('family_relatives', [...(formData.family_relatives || []), { name: '' }])}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Adicionar familiar
              </Button>
            </Grid>
          </Grid>
        )}

        {formTabValue === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Rua" value={formData.address_street} onChange={(e) => setField('address_street', e.target.value)} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_street || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Número" value={formData.address_number} onChange={(e) => setField('address_number', e.target.value)} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_number || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Complemento" value={formData.address_complement} onChange={(e) => setField('address_complement', e.target.value)} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_complement || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Bairro" value={formData.address_neighborhood} onChange={(e) => setField('address_neighborhood', e.target.value)} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_neighborhood || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth label="Cidade" value={formData.address_city} onChange={(e) => setField('address_city', e.target.value)} size="small" InputLabelProps={{ shrink: Boolean(String(formData.address_city || '').trim()) }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel shrink={Boolean(String(formData.address_state || '').trim())}>UF</InputLabel>
                <Select value={formData.address_state} label="UF" onChange={(e) => setField('address_state', e.target.value)}>
                  {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                    <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={postalCodeLabel()}
                value={formData.address_zip}
                onChange={(e) => handleCepChange(e.target.value)}
                size="small"
                placeholder={isPortugalMarket() ? '0000-000' : '00000-000'}
                error={!!cepError}
                helperText={cepError || (cepLoading ? 'Buscando endereço…' : '')}
                InputLabelProps={{ shrink: Boolean(String(formData.address_zip || '').trim()) }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {cepLoading
                        ? <CircularProgress size={16} />
                        : <SearchIcon fontSize="small" sx={{ color: PATIENT_FORM_THEME.slateFaint, cursor: 'pointer' }} onClick={() => fetchCep(formData.address_zip)} />}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        )}

        {formTabValue === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Tipo Sanguíneo</InputLabel>
                <Select value={formData.blood_type} label="Tipo Sanguíneo" onChange={(e) => setField('blood_type', e.target.value)}>
                  {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bt => <MenuItem key={bt} value={bt}>{bt}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Alergias (separar por vírgula)" value={(formData.allergies || []).join(', ')} onChange={(e) => setField('allergies', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} size="small" multiline rows={2} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Condições Crônicas (separar por vírgula)" value={(formData.chronic_conditions || []).join(', ')} onChange={(e) => setField('chronic_conditions', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} size="small" multiline rows={2} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Medicamentos em Uso (separar por vírgula)" value={(formData.medications || []).join(', ')} onChange={(e) => setField('medications', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} size="small" multiline rows={2} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Observações Médicas" value={formData.medical_notes} onChange={(e) => setField('medical_notes', e.target.value)} size="small" multiline rows={3} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Contato Emergência - Nome" value={formData.emergency_contact_name} onChange={(e) => setField('emergency_contact_name', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Contato Emergência - Telefone" value={formData.emergency_contact_phone} onChange={(e) => setField('emergency_contact_phone', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Contato Emergência - Parentesco" value={formData.emergency_contact_relation} onChange={(e) => setField('emergency_contact_relation', e.target.value)} size="small" /></Grid>
          </Grid>
        )}

        {formTabValue === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12}><Typography variant="body2" sx={patientFormCaptionSx}>Aferições da pré-consulta.</Typography></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="PA (Pressão Arterial)" value={formData.vital_blood_pressure} onChange={(e) => setField('vital_blood_pressure', e.target.value)} size="small" placeholder="Ex: 120/80" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="FC (Frequência Cardíaca)" value={formData.vital_heart_rate} onChange={(e) => setField('vital_heart_rate', e.target.value)} size="small" placeholder="bpm" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Glicemia" value={formData.vital_glucose} onChange={(e) => setField('vital_glucose', e.target.value)} size="small" placeholder="mg/dL" /></Grid>
          </Grid>
        )}

        {formTabValue === 4 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Convênio / Plano" value={formData.insurance_plan} onChange={(e) => setField('insurance_plan', e.target.value)} size="small" placeholder="Opcional — deixe em branco se for particular" helperText="Paciente particular pode ficar sem convênio" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nº do Cartão/Matrícula" value={formData.insurance_number} onChange={(e) => setField('insurance_number', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Validade" type="date" value={formData.insurance_validity} onChange={(e) => setField('insurance_validity', e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={dateInputFieldSx} /></Grid>
          </Grid>
        )}

        {formTabValue === 5 && (
          <Grid container spacing={2}>
            <Grid item xs={12}><Typography variant="body2" sx={{ ...patientFormCaptionSx, mb: 1 }}>Preencher apenas para pacientes menores de idade</Typography></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nome do Responsável" value={formData.guardian_name} onChange={(e) => setField('guardian_name', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label={taxIdGuardianLabel()} value={formData.guardian_cpf} onChange={(e) => setField('guardian_cpf', formatTaxIdInput(e.target.value))} size="small" helperText="Opcional — pode informar depois" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Telefone do Responsável" value={formData.guardian_phone} onChange={(e) => setField('guardian_phone', e.target.value)} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Parentesco" value={formData.guardian_relation} onChange={(e) => setField('guardian_relation', e.target.value)} size="small" /></Grid>
          </Grid>
        )}

        {isOphthalmology && formTabValue === 6 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Motivo da consulta" value={formData.op_reason} onChange={(e) => setField('op_reason', e.target.value)} size="small" multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={patientFormSectionTitleSx}>Antecedentes</Typography>
              <FormGroup row>
                <FormControlLabel control={<Checkbox checked={formData.op_has} onChange={(e) => setField('op_has', e.target.checked)} sx={patientFormCheckboxSx} />} label="HAS" />
                <FormControlLabel control={<Checkbox checked={formData.op_dm} onChange={(e) => setField('op_dm', e.target.checked)} sx={patientFormCheckboxSx} />} label="DM" />
                <FormControlLabel control={<Checkbox checked={formData.op_glaucoma} onChange={(e) => setField('op_glaucoma', e.target.checked)} sx={patientFormCheckboxSx} />} label="Glaucoma" />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={patientFormSectionTitleSx}>FACO</Typography>
              <FormGroup row>
                <FormControlLabel control={<Checkbox checked={formData.op_faco_od} onChange={(e) => setField('op_faco_od', e.target.checked)} sx={patientFormCheckboxSx} />} label="OD" />
                <FormControlLabel control={<Checkbox checked={formData.op_faco_oe} onChange={(e) => setField('op_faco_oe', e.target.checked)} sx={patientFormCheckboxSx} />} label="OE" />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="HF (História Familiar)" value={formData.op_hf} onChange={(e) => setField('op_hf', e.target.value)} size="small" multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={patientFormSectionTitleSx}>AV (Acuidade Visual)</Typography>
              <FormGroup row>
                <FormControlLabel control={<Checkbox checked={formData.op_av_cc} onChange={(e) => setField('op_av_cc', e.target.checked)} sx={patientFormCheckboxSx} />} label="CC (com correção)" />
                <FormControlLabel control={<Checkbox checked={formData.op_av_sc} onChange={(e) => setField('op_av_sc', e.target.checked)} sx={patientFormCheckboxSx} />} label="SC (sem correção)" />
              </FormGroup>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={patientFormDialogFooterSx}>
        <Button fullWidth={isMobile} onClick={onClose} sx={patientFormCancelButtonSx}>Cancelar</Button>
        <Button fullWidth={isMobile} variant="contained" onClick={handleSave} disabled={saving}
          sx={patientFormPrimaryButtonSx}>
          {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : editingId ? 'Atualizar' : 'Cadastrar'}
        </Button>
      </DialogActions>
      <PatientSaveErrorDialog
        open={Boolean(saveError)}
        error={saveError}
        onClose={() => setSaveError(null)}
      />
    </Dialog>
    </>
  );
};

export default PatientFormDialog;
